import { HttpClient, HttpEvent, HttpEventType, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { HttpResponsesHandlersFactory } from "@bbwt/modules/data-service/http-responses-handler";
import { IOrganization, OrganizationAppointmentBookingInfo, OrganizationSelfRegisterInfo } from "./organization";
import { PagedCrudService } from "@features/grid";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import * as signalR from "@microsoft/signalr";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { PreferredPharmacyDetails } from "./preferred-pharmacy-details";
import { Observable } from "rxjs";
import { UserWithRoles } from "./user-roles";
import { IRole } from "@main/roles";
import { SearchUserItem } from "@mipro/classes/search-user-item";
import { IAddress } from "@main/companies/address";

@Injectable({
    providedIn: "root"
})
export class OrganizationService extends PagedCrudService<IOrganization> {
    static readonly OrganizationChangedEventName = "Organization Changed";
    static readonly ModulesChangedEventName = "ModulesChanged";
    static readonly FunctionalGutOrgName = "Functional Gut Tests";

    readonly url = "api/company";
    readonly entityTitle = this.namingConvService.organisationSingular;

    private _hubConnection: signalR.HubConnection;

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private broadcastService: BroadcastService,
        public namingConvService: NamingConventionService
    ) {
        super(http, handlersFactory);
        this.initHubConnection();
    }

    protected initHubConnection(): void {
        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl("api/company")
            .configureLogging(signalR.LogLevel.None)
            .withAutomaticReconnect()
            .build();

        this._hubConnection.start();

        this._hubConnection.on(OrganizationService.ModulesChangedEventName, orgId =>
            this.broadcastService.broadcast(OrganizationService.ModulesChangedEventName, orgId)
        );
    }

    update(id: string, item: IOrganization): Promise<IOrganization> {
        return super.update(id, item).then(result => {
            this.broadcastService.broadcast(OrganizationService.OrganizationChangedEventName, result);
            return result;
        });
    }

    exists(company: any): Promise<boolean> {
        return this.handleRequest(this.http.post<boolean>(`${this.url}/exists`, company), this.handlersFactory.getForReadAll());
    }

    externalIdExists(company: any): Promise<boolean> {
        return this.handleRequest(this.http.post<boolean>(`${this.url}/external-id-exists`, company), this.handlersFactory.getForReadAll());
    }

    getAllPlain(): Promise<IOrganization[]> {
        return this.handleRequest<IOrganization[]>(
            this.http.get<IOrganization[]>(`${this.url}/all-plain`),
            this.handlersFactory.getForReadAll()
        );
    }

    getMiproModules() {
        return this.handleRequest(this.http.get<string[]>(`${this.url}/mipro-modules`), this.handlersFactory.getDefault());
    }

    getSelfRegistrationUrl(organizationId: string, organizationName: string): Promise<{ selfRegistrationURL: string }> {
        return this.handleRequest(
            this.http.get<{ selfRegistrationURL: string }>(`${this.url}/self-register-patient-url/${organizationId}/${organizationName}`)
        );
    }

    isSelfRegistrationEnabled(organizationURL: string): Promise<OrganizationSelfRegisterInfo> {
        return this.handleRequest(
            this.http.get<OrganizationSelfRegisterInfo>(`${this.url}/is-self-registration-enabled/${organizationURL}`),
            this.handlersFactory.getDefault()
        );
    }

    getCurrentOrganizationAppointmentBookingUrl(): Promise<{ appointmentBookingURL: string, appointmentBookingURLEnabled: boolean }> {
        return this.handleRequest(
            this.http.get<{ appointmentBookingURL: string, appointmentBookingURLEnabled: boolean }>(
                `${this.url}/current-organization-appointment-booking-url`)
        );
    }

    getAppointmentBookingUrl(organizationId: string, organizationName: string): Promise<{ appointmentBookingURL: string }> {
        return this.handleRequest(
            this.http.get<{ appointmentBookingURL: string }>(`${this.url}/appointment-booking-url/${organizationId}/${organizationName}`)
        );
    }

    isAppointmentBookingEnabled(organizationURL: string): Promise<OrganizationAppointmentBookingInfo> {
        return this.handleRequest(
            this.http.get<OrganizationAppointmentBookingInfo>(`${this.url}/is-appointment-booking-enabled/${organizationURL}`),
            this.handlersFactory.getDefault()
        );
    }

    getAppointmentBookingInfo(organizationId: string): Promise<OrganizationAppointmentBookingInfo> {
        return this.handleRequest(
            this.http.get<OrganizationAppointmentBookingInfo>(`${this.url}/appointment-booking-info/${organizationId}`),
            this.handlersFactory.getDefault()
        );
    }

    getCurrentOrganizationAppointmentBookingInfo(): Promise<OrganizationAppointmentBookingInfo> {
        return this.handleRequest(
            this.http.get<OrganizationAppointmentBookingInfo>(`${this.url}/appointment-booking-info`),
            this.handlersFactory.getDefault()
        );
    }

    getSelfRegistrationInfo(organizationId: string): Promise<OrganizationSelfRegisterInfo> {
        return this.handleRequest(
            this.http.get<OrganizationSelfRegisterInfo>(`${this.url}/self-registration-info/${organizationId}`),
            this.handlersFactory.getDefault()
        );
    }

    getCurrentSelfRegistrationInfo(): Promise<OrganizationSelfRegisterInfo> {
        return this.handleRequest(
            this.http.get<OrganizationSelfRegisterInfo>(`${this.url}/current-self-registration-info`),
            this.handlersFactory.getDefault()
        );
    }

    getCurrentPreferredPharmacyDetails() {
        return this.handleRequest(
            this.http.get<PreferredPharmacyDetails>(`${this.url}/preferred-pharmacy`),
            this.handlersFactory.getDefault()
        );
    }

    getCurrentAddress(): Promise<IAddress> {
        return this.handleRequest(
            this.http.get<IAddress>(`${this.url}/address`),
            this.handlersFactory.getDefault()
        );
    }

    getEnablePatientPsdConfirmation() {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/enable-patient-psd-confirmation`),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    isMandatoryIdentityVerificationEnabled(organizationIds: number[]): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-mandatory-identity-verification-enabled`, {
                params: { organizationIds }
            }),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    isAutoApproveSelfRegisteredPatientsEnabledByOrgId(organizationId: string): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-auto-approve-self-registered-patients-by-id/${organizationId}`),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    isAutoApproveSelfRegisteredPatientsEnabledByOrgUrl(orgSelfRegistrationURL: string): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-auto-approve-self-registered-patients-by-url/${orgSelfRegistrationURL}`),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    async addPatientOrganizationFromSelfRegister(patientId: string, organizationId: number): Promise<void> {
        const patientOrg = { patientId: patientId, organizationId: organizationId };
        return this.handleRequest(
            this.http.post<void>(`${this.url}/add-patient-organization-from-self-register`, patientOrg),
            this.handlersFactory.getForReadAll()
        );
    }

    async getBySelfRegistrationUrl(orgSelfRegistrationURL: string): Promise<IOrganization> {
        return this.handleRequest(
            this.http.get<IOrganization>(`${this.url}/get-organization-url/${orgSelfRegistrationURL}`),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    uploadLogoImage(organizationId: string, formData: FormData): Observable<HttpEvent<any>> {
        const uploadRequest = this.http.request(
            new HttpRequest("POST", `${this.url}/${organizationId}/upload-logo-image`, formData, { reportProgress: true })
        );

        // Broadcast an event after the upload request completes
        uploadRequest.subscribe({
            next: (event) => {
                if (event.type === HttpEventType.Response) {
                    this.broadcastService.broadcast(OrganizationService.OrganizationChangedEventName, organizationId);
                }
            }
        });

        return uploadRequest;
    }

    async getUsersByOrganization(organizationId: string): Promise<UserWithRoles[]> {
         return this.handleRequest(
            this.http.get<UserWithRoles[]>(`${this.url}/${organizationId}/users`),
            this.handlersFactory.getDefault()
        );
    }

    async updateNHSAccessStatusUser(userId: string, organizationId: string): Promise<void> {
        return this.handleRequest(
            this.http.post<void>(`${this.url}/add-nhs-access-user/${organizationId}/${userId}`, null),
            this.handlersFactory.getForReadAll()
        );
    }

    getAllowCliniciansModifyClinics(): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/allow-clinicians-modify-clinics`),
            this.handlersFactory.getDefault()
        );
    }

    async updateAllowCliniciansModifyClinics(allowCliniciansModifyClinics: boolean): Promise<void> {
        return this.handleRequest(
            this.http.post<void>(`${this.url}/update-allow-clinicians-modify-clinics/${allowCliniciansModifyClinics}`, null),
            this.handlersFactory.getForReadAll()
        );
    }

    getClinicianUserTypesFromCurrentOrganization(): Promise<IRole[]> {
        return this.httpGet<IRole[]>("clinician-usertypes");
    }

    getOrganizations(organizationIds: number[]): Promise<IOrganization[]> {
        return this.handleRequest(
                this.http.get<IOrganization[]>(`${this.url}/get-organization-by-id`, {
                params: { organizationIds }
            }),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        )
    }

    getClinicianUserTypes(organizationId: string): Promise<IRole[]> {
        return this.httpGet<IRole[]>(`${organizationId}/clinician-usertypes`);
    }

    getClinicians(organizationId: string, fullname?: string, userType?: string) {
        let params = new HttpParams();

        params = !!userType ? params.append("userTypeId", userType) : params;
        params = !!fullname ? params.append("fullName", fullname) : params;

        return this.handleRequest(
            this.http.get<SearchUserItem[]>(`${this.url}/${organizationId}/clinicians`, { params }),
            this.handlersFactory.getDefault()
        );
    }

    async getExternalSiteURL(): Promise<{ externalSiteURL: string }> {
        return this.handleRequest<{ externalSiteURL: string }>(
            this.http.get<{ externalSiteURL: string }>(`${this.url}/external-site-url`),
            this.handlersFactory.getForReadAll()
        );
    }

    isPatientUploadDocumentEnabled(): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-patient-upload-document-enabled`),
            this.handlersFactory.getDefault()
        );
    }
}
